import { useImperativeHandle, forwardRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import { baseVirtualizedListPropTypes, domRefPropType } from '../propTypes';

const useStyles = makeStyles(() => ({
  root: {
    boxSizing: 'border-box',
    display: ({ usesExternalScroll }) =>
      usesExternalScroll ? 'block' : 'flex',
    flexDirection: 'row',
    position: 'relative',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    height: ({ usesExternalScroll }) => (usesExternalScroll ? 'auto' : '100%'),
    overflow: 'visible',
  },
  scrollableViewport: {
    pointerEvents: 'auto',
    scrollbarGutter: 'stable',
    '-webkit-overflow-scrolling': 'touch',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    height: ({ usesExternalScroll }) => (usesExternalScroll ? 'auto' : '100%'),
    overflow: ({ usesExternalScroll }) =>
      usesExternalScroll ? 'visible' : 'auto',
  },
  itemsWrapper: {
    willChange: 'transform',
    backfaceVisibility: 'hidden',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    position: 'relative',
  },
  item: {
    boxSizing: 'border-box',
    position: 'absolute',
    top: 0,
    left: 0,
    willChange: 'transform',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    contain: ({ isDynamicSize }) =>
      isDynamicSize ? undefined : 'layout style paint',
    overflow: ({ isDynamicSize }) => (isDynamicSize ? 'visible' : 'hidden'),
  },
}));

function VirtualizedListViewComponent(
  {
    className = undefined,
    viewportClass = undefined,
    itemClass = undefined,
    virtualizerRef,
    items,
    isDynamicSize,
    usesExternalScroll,
    virtualizer,
    hasNextPage = false,
    isNextPageLoading = false,
    renderItem = undefined,
    onLoadMore = undefined,
    viewportRef,
    scrollWrapper: ScrollWrapper = undefined,
    scrollWrapperProps = undefined,
  },
  ref
) {
  const classes = useStyles({ usesExternalScroll, isDynamicSize });

  useImperativeHandle(ref, () => viewportRef.current);

  useEffect(() => {
    if (virtualizerRef) virtualizerRef.current = virtualizer;
  }, [virtualizer, virtualizerRef]);

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    const lastItem = virtualItems[virtualItems.length - 1];

    if (
      !virtualizer.isScrolling ||
      !lastItem ||
      !hasNextPage ||
      isNextPageLoading ||
      !onLoadMore
    )
      return;

    if (lastItem.index >= items.length - 1) {
      onLoadMore();
    }
  }, [
    virtualItems,
    items.length,
    virtualizer.isScrolling,
    hasNextPage,
    isNextPageLoading,
    onLoadMore,
  ]);

  useEffect(() => {
    if (usesExternalScroll) return undefined;

    const el = viewportRef.current;
    if (!el) return undefined;

    const handleMouseWheel = e => {
      const isScrollable = el.scrollHeight > el.clientHeight;

      if (isScrollable) {
        e.stopPropagation();
      }
    };

    el.addEventListener('wheel', handleMouseWheel, { passive: true });
    return () => el.removeEventListener('wheel', handleMouseWheel);
  }, [usesExternalScroll, viewportRef]);

  const viewport = (
    <div
      ref={viewportRef}
      className={classNames(classes.scrollableViewport, viewportClass)}
    >
      <div
        className={classes.itemsWrapper}
        style={{
          height: `${virtualizer.getTotalSize()}px`,
        }}
      >
        {virtualItems.map(virtualItem => (
          <div
            key={virtualItem.key}
            data-index={virtualItem.index}
            ref={isDynamicSize ? virtualizer.measureElement : undefined}
            className={classNames(classes.item, itemClass)}
            style={{
              height: isDynamicSize ? undefined : `${virtualItem.size}px`,
              transform: `translateY(${
                virtualItem.start -
                (usesExternalScroll ? virtualizer.options.scrollMargin : 0)
              }px)`,
            }}
          >
            {renderItem(items[virtualItem.index], virtualItem, virtualizer)}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className={classNames(classes.root, className)}>
      {ScrollWrapper ? (
        <ScrollWrapper {...scrollWrapperProps}>{viewport}</ScrollWrapper>
      ) : (
        viewport
      )}
    </div>
  );
}

const VirtualizedListView = forwardRef(VirtualizedListViewComponent);

VirtualizedListView.displayName = 'VirtualizedListView';

VirtualizedListView.propTypes = {
  ...baseVirtualizedListPropTypes,
  usesExternalScroll: PropTypes.bool.isRequired,
  virtualizer: PropTypes.object.isRequired,
  viewportRef: domRefPropType.isRequired,
  scrollWrapper: PropTypes.elementType,
  scrollWrapperProps: PropTypes.object,
};

export default VirtualizedListView;
